import { ReactNode } from 'react';

/**
 * ActivityPanel — right-side sticky list of recent activity events.
 *
 * Derived from M3 List — Supporting pane variant per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/lists/overview (supporting pane layout)
 *   - Reference: github.com/material-components/material-web (no first-party
 *                supporting-pane component at time of authoring; M3 spec drives
 *                the layout pattern)
 *
 * Populus adaptations applied:
 *   - Right-sticky on desktop (Christophe's entity-detail screens convention)
 *   - Per-event: colored dot (semantic) + content + timestamp
 *   - Compact density: 12px vertical padding per event (matches table row rhythm)
 *   - Empty state with helpful message
 *
 * Token discipline:
 *   - Panel surface → --color-card (system; operator-tool chrome)
 *   - Event dot colors → --color-status-* (semantic; never per-org)
 *   - Title bar, borders, text → --color-* (system)
 *
 * Phase 11 Block 6 PR 3c (2026-05-12).
 */

export interface ActivityEvent {
  id: string;
  kind?: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  title: ReactNode;
  description?: ReactNode;
  timestamp: string;          // e.g., "Apr 30, 2026 8:27 PM"
}

export interface ActivityPanelProps {
  title?: ReactNode;
  events: ActivityEvent[];
  emptyMessage?: ReactNode;
}

const dotColor: Record<NonNullable<ActivityEvent['kind']>, string> = {
  success: 'var(--color-status-green)',
  info: 'var(--color-status-celadon)',
  warning: 'var(--color-status-amber)',
  danger: 'var(--color-status-red)',
  neutral: 'var(--color-status-gray)',
};

export function ActivityPanel({
  title = 'Recent activity',
  events,
  emptyMessage = 'No activity yet.',
}: ActivityPanelProps) {
  return (
    <aside style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      boxShadow: 'var(--sh-sm)',
      overflow: 'hidden',
      width: '100%',
    }}>
      <header style={{
        padding: 'var(--sp-3) var(--sp-4)',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-md)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--color-ink)',
        }}>{title}</h3>
      </header>
      <div>
        {events.length === 0 ? (
          <div style={{
            padding: 'var(--sp-5) var(--sp-4)',
            color: 'var(--color-muted)',
            fontSize: 'var(--fs-sm)',
            textAlign: 'center',
          }}>{emptyMessage}</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {events.map(ev => (
              <li
                key={ev.id}
                style={{
                  display: 'flex',
                  gap: 'var(--sp-2)',
                  padding: 'var(--sp-3) var(--sp-4)',
                  borderBottom: '1px solid var(--color-border)',
                }}
              >
                <span style={{
                  width: 8, height: 8,
                  borderRadius: '50%',
                  background: dotColor[ev.kind ?? 'neutral'],
                  flexShrink: 0,
                  marginTop: 6,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 'var(--fs-md)',
                    color: 'var(--color-ink)',
                    fontWeight: 'var(--fw-medium)',
                  }}>{ev.title}</div>
                  {ev.description && (
                    <div style={{
                      fontSize: 'var(--fs-sm)',
                      color: 'var(--color-ink-3)',
                      marginTop: 'var(--sp-0-5)',
                    }}>{ev.description}</div>
                  )}
                  <div style={{
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--color-muted)',
                    marginTop: 'var(--sp-1)',
                  }}>{ev.timestamp}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
